import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import TierApprovalDialog from "../components/TierApprovalDialog";
import { Gavel, XCircle, KeyRound, ShieldAlert } from "lucide-react";

export default function Approvals() {
  const [proposals, setProposals] = useState([]);
  const [dialog, setDialog] = useState({ open: false, proposal: null });

  const load = async () => {
    const { data } = await api.get("/approvals");
    setProposals(data);
  };
  useEffect(() => { load(); }, []);

  const reject = async (pid) => {
    await api.post(`/approvals/${pid}/reject`, {});
    toast.success("Proposal rejected.");
    load();
  };

  const openApprove = (p) => setDialog({ open: true, proposal: p });

  const confirmApprove = async (passphrase, totp) => {
    const p = dialog.proposal;
    await api.post(`/approvals/${p.id}/approve`, { passphrase, totp_code: totp });
    toast.success("Proposal approved.");
    setDialog({ open: false, proposal: null });
    load();
  };

  return (
    <div data-testid="approvals-page">
      <div className="mb-8">
        <div className="overline">Approval gate</div>
        <h1 className="font-display text-4xl mt-2">Pending proposals.</h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Nothing changes without your sign-off. Tier A proposals need your passphrase.
          Tier B proposals (hard-limit changes) need passphrase and your 2FA code.
        </p>
      </div>

      <div className="space-y-5">
        {proposals.length === 0 && (
          <div className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-faint)" }}>
            No proposals waiting.
          </div>
        )}
        {proposals.map((p) => {
          const tierB = p.tier === "B";
          return (
            <article key={p.id} data-testid={`proposal-${p.id}`}
                     className="elle-surface p-7 rounded-sm border"
                     style={{ borderColor: tierB ? "var(--elle-oxblood)" : "var(--elle-border)" }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 overline"
                       style={{ color: tierB ? "var(--elle-oxblood-bright)" : "var(--elle-amber)" }}>
                    {tierB ? <ShieldAlert size={14}/> : <KeyRound size={14}/>}
                    Tier {p.tier}
                    <span className="font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                      · {p.target.replace("_", " ")} · {p.data_points} data points
                    </span>
                    {p.change_payload && (
                      <span data-testid={`auto-apply-${p.id}`}
                            className="font-mono-elle px-2 py-0.5 rounded-sm text-[0.65rem]"
                            style={{ background: "rgba(61,90,64,0.15)", color: "var(--elle-moss)",
                                     letterSpacing: "0.12em" }}>
                        AUTO-APPLIES
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-2xl mt-2">{p.title}</h3>
                </div>
                <div className="text-xs font-mono-elle shrink-0" style={{ color: "var(--elle-ink-faint)" }}>
                  {new Date(p.created_at).toLocaleString()}
                </div>
              </div>
              <dl className="mt-5 grid md:grid-cols-2 gap-5 text-sm">
                <div><dt className="overline mb-1">Observation</dt><dd style={{ color: "var(--elle-ink)" }}>{p.observation}</dd></div>
                <div><dt className="overline mb-1">Conclusion</dt><dd style={{ color: "var(--elle-ink)" }}>{p.conclusion}</dd></div>
                <div><dt className="overline mb-1">Proposed change</dt><dd style={{ color: "var(--elle-ink)" }}>{p.proposed_change}</dd></div>
                <div><dt className="overline mb-1">Expected impact</dt><dd style={{ color: "var(--elle-ink)" }}>{p.expected_impact}</dd></div>
              </dl>
              <div className="mt-6 flex gap-2">
                <Button data-testid={`approve-btn-${p.id}`} onClick={() => openApprove(p)}
                        className={tierB ? "btn-oxblood rounded-sm" : "btn-amber rounded-sm"}>
                  <Gavel size={14} className="mr-2"/> Approve (Tier {p.tier})
                </Button>
                <Button data-testid={`reject-btn-${p.id}`} onClick={() => reject(p.id)}
                        variant="outline" className="btn-ghost rounded-sm">
                  <XCircle size={14} className="mr-2"/> Reject
                </Button>
              </div>
            </article>
          );
        })}
      </div>

      <TierApprovalDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
        tier={dialog.proposal?.tier || "A"}
        title={dialog.proposal?.title || ""}
        description={dialog.proposal?.proposed_change || ""}
        onConfirm={confirmApprove}
      />
    </div>
  );
}
